import { Component, ViewChild } from '@angular/core';
import { ButtonComponent } from 'components/input/button/button.component';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { ProductPlanTagConstants } from 'constants/ProductPlanTagConstants';
import { CredentialType } from 'enums/CredentialType';
import { HubAction } from 'enums/HubAction';
import { ProductPlanSettingType } from 'enums/ProductPlanSettingType';
import { ProductPlanSettingModel } from 'models/generated/Entities.GormCrm.ProductPlanSetting';
import { FreeProductPlanSettingDtoModel } from 'models/generated/Models.FreeProductPlanSettingDto';
import { KeepExistingProductPlanOnDiscontinuationSettingDtoModel } from 'models/generated/Models.KeepExistingProductPlanOnDiscontinuationSettingDto';
import { SelectListItemModel } from 'models/SelectList';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { TranslatePipe } from 'pipes/translate.pipe';
import { CredentialRequired } from 'services/credential.service';
import { FormValidationService } from 'services/form-validation.service';
import { ComponentUrl, NavigationService, UrlParam } from 'services/navigation.service';
import { ProductPlanService } from 'services/productplan.service';
import { PushService } from 'services/push.service';
import { FreeProductPlanSettingComponent } from './free-product-plan-setting.component';
import { KeepExistingProductPlanOnDiscontinuationComponent } from './keep-existing-product-plan-on-discontinuation.component';

@Component({
	selector: 'product-plan-settings',
	templateUrl: './product-plan-settings.component.html',
	providers: [FormValidationService],
	imports: [Layout, ListView, CustomSelectComponent, CustomInputComponent, FreeProductPlanSettingComponent, KeepExistingProductPlanOnDiscontinuationComponent, TranslatePipe]
})
@ComponentUrl('product-plan-settings')
@CredentialRequired(CredentialType.EditProductPlanSettings)
export class ProductPlanSettingsComponent {

	@ViewChild('upsertButton') upsertButton!: ButtonComponent;

	private _selectedId = '';
	@UrlParam
	set selectedId(id: string) {
		this._selectedId = id;
		this.navigation.refreshUrl();
	}
	get selectedId() {
		return this._selectedId;
	}

	private _selectedSetting?: ProductPlanSettingModel;
	set selectedSetting(setting: ProductPlanSettingModel | undefined) {
		this._selectedSetting = setting;
		this.selectedId = setting?.id ?? '';
		this.selectedSettingName = setting?.name ?? '';
		this.serializeDataToSetting();
	}
	get selectedSetting() {
		return this._selectedSetting;
	}

	selectListTags: SelectListItemModel[] = [];
	tags: string[] = [];
	settings?: ProductPlanSettingModel[];
	settingType = ProductPlanSettingType;
	settingTypeItems = Array.from(ProductPlanSettingType.getKeys().keys()).map(key => new SelectListItemModel(key, this.translate.transform('enum.product-plan-settings-type.' + this.settingType[key])));
	selectedSettingName = '';

	freeProductPlanSetting = new FreeProductPlanSettingDtoModel();
	keepExistingProductPlanOnDiscontinuationSetting = new KeepExistingProductPlanOnDiscontinuationSettingDtoModel();

	constructor(private readonly productPlanService: ProductPlanService, private readonly pushService: PushService, private readonly translate: TranslatePipe, private readonly navigation: NavigationService) {
		this.loadSettings();

		this.pushService.onProductPlanSettingChanges.subscribe(event => {
			if (event.action == HubAction.Delete) this.selectedId = '';
			this.navigation.refreshView(ProductPlanSettingsComponent).then(view => view.selectedSetting = this.selectedSetting);
		});

		this.productPlanService.getAllProductPlanTags().then(tags => {
			this.tags = [...new Set(tags.concat(ProductPlanTagConstants.values))];
			this.selectListTags = tags.map((tag: string) => new SelectListItemModel(tag, tag));
		});
	}

	loadSettings() {
		this.productPlanService.getProductPlanSettings().then(settings => {
			this.settings = settings;
			this.selectedSetting = settings.find(s => s.id == this.selectedId) ?? undefined;
		});
	}

	createEmptySetting() {
		this.selectedSetting = new ProductPlanSettingModel();
	}

	upsertSetting() {
		if (!this.selectedSetting) return;

		this.serializeSettingToData();
		this.selectedSetting.name = this.selectedSettingName;

		this.productPlanService.upsertProductPlanSetting(this.selectedSetting).then(_ => this.upsertButton.reset());
	}

	deleteSetting() {
		if (!this.selectedSetting) return;
		this.productPlanService.deleteProductPlanSetting(this.selectedSetting.id!);
	}

	serializeDataToSetting() {
		this.freeProductPlanSetting = new FreeProductPlanSettingDtoModel();
		this.keepExistingProductPlanOnDiscontinuationSetting = new KeepExistingProductPlanOnDiscontinuationSettingDtoModel();

		switch (this.selectedSetting?.type) {
			case ProductPlanSettingType.FreeProductPlan:
				this.freeProductPlanSetting = JSON.parse(this.selectedSetting.data);
				break;
				
			case ProductPlanSettingType.KeepExistingProductPlanOnDiscontinuation:
				this.keepExistingProductPlanOnDiscontinuationSetting = JSON.parse(this.selectedSetting.data);
				break;
		}
	}

	serializeSettingToData() {
		switch (this.selectedSetting?.type) {
			case ProductPlanSettingType.FreeProductPlan:
				this.selectedSetting.data = this.serialize(this.freeProductPlanSetting);
				break;
				
			case ProductPlanSettingType.KeepExistingProductPlanOnDiscontinuation:
				this.selectedSetting.data = this.serialize(this.keepExistingProductPlanOnDiscontinuationSetting);
				break;
		}
	}

	serialize(obj: any) {
		let data = obj;
		if (data._includedRelationProperties) data._includedRelationProperties = undefined;
		return JSON.stringify(data);
	}
}
